// API Client for CPG Simulator

const API_BASE = import.meta.env.VITE_API_URL || '';

interface FetchOptions extends RequestInit {
  params?: Record<string, string>;
}

class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

async function fetchJson<T>(path: string, options: FetchOptions = {}): Promise<T> {
  const { params, ...fetchOptions } = options;

  let url = `${API_BASE}${path}`;
  if (params) {
    const searchParams = new URLSearchParams(params);
    url += `?${searchParams.toString()}`;
  }

  const response = await fetch(url, {
    ...fetchOptions,
    headers: {
      'Content-Type': 'application/json',
      ...fetchOptions.headers,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new ApiError(response.status, errorData.error || `HTTP ${response.status}`);
  }

  return response.json();
}

// Types
export interface ClientSummary {
  client_id: string;
  client_name: string;
  market: {
    gpc_brick: number;
    country: string;
  };
  enabled_modules: Array<{
    name: string;
    tier: 'basic' | 'advanced';
  }>;
}

export interface MarketOverview {
  market: {
    total_value: number;
    total_volume: number;
    growth_rate: number;
    currency: string;
  };
  overview?: {
    total_value: number;
    total_volume: number;
    value_growth: number;
    volume_growth: number;
  };
  total_brands: number;
  top_brands: Array<{
    name: string;
    share: number;
    volume: number;
    manufacturer?: string;
    growth?: number;
  }>;
  growth_outlook: string;
  market_value_formatted?: string;
  market_volume_formatted?: string;
}

export interface AttributePreference {
  attribute: string;
  attribute_es?: string;
  levels: Array<{
    level: string;
    level_es?: string;
    preference: number; // 0-1 representing preference share
  }>;
}

export interface SegmentAnalysis {
  segments: Array<{
    id: string;
    name: string;
    share: number;
    growth: number;
    characteristics: string[];
    price_sensitivity?: number;
    brand_loyalty?: number;
    population?: number;
    attribute_preferences?: AttributePreference[];
  }>;
  market_summary?: {
    total_consumers: number;
    market_penetration: number;
    category_growth: number;
  };
}

export interface PortfolioPosition {
  rank: number;
  total_share: number;
  strengths: string[];
  weaknesses: string[];
  opportunities: string[];
  threats: string[];
}

export interface ProductElasticity {
  gtin: string;
  name: string;
  // API returns own_elasticity from pricing engine
  own_elasticity?: number;
  elasticity?: number;
  classification?: 'elastic' | 'inelastic' | 'unit_elastic' | 'highly_elastic';
  confidence?: number;
  confidence_interval?: [number, number];
  data_quality?: 'high' | 'medium' | 'low' | 'estimated';
  interpretation?: {
    sensitivity: 'inelastic' | 'unit_elastic' | 'elastic' | 'highly_elastic';
    implication: string;
    recommendation: string;
  };
  by_segment?: Array<{
    segment_id: string;
    segment_name: string;
    elasticity: number;
    segment_share: number;
  }>;
}

export interface PortfolioPricing {
  products: Array<{
    gtin: string;
    name: string;
    current_price: number;
    optimal_price: number;
    elasticity: number;
    price_gap: number;
  }>;
  // API may return either architecture or portfolio_summary
  architecture?: {
    price_range: { min: number; max: number };
    average_price: number;
    price_variance: number;
  };
  portfolio_summary?: {
    price_range: [number, number];
    weighted_avg_price: number;
    product_count: number;
  };
}

export interface DemandCurve {
  gtin?: string;
  product_gtin?: string;
  product_name?: string;
  current?: {
    price: number;
    quantity: number;
    revenue: number;
  };
  curve_points: Array<{
    price: number;
    quantity: number;
    revenue?: number;
  }>;
  revenue_curve?: Array<{
    price: number;
    revenue: number;
    profit_estimate?: number;
  }>;
  optimal: {
    price: number;
    quantity: number;
    revenue: number;
  };
  key_prices?: {
    revenue_maximizing: number;
    profit_maximizing: number;
  };
}

export interface LaunchConfig {
  client_id: string;
  market: { gpc_brick: number; country: string };
  default_assumptions: {
    trial_rate: number;
    repeat_rate: number;
    distribution_month3: number;
    distribution_month6: number;
    distribution_month12: number;
  };
}

export interface LaunchForecast {
  verdict: 'go' | 'refine' | 'pause';
  confidence: number;
  year1_volume: number;
  year1_revenue: number;
  market_share: number;
  source_of_volume: {
    market_growth: number;
    competitor: number;
    new_users: number;
    own_portfolio: number;
  };
  cannibalization_risk: 'low' | 'medium' | 'high';
}

export interface PortfolioHealth {
  hhi: number;
  health_score: number;
  sku_count: number;
  total_skus?: number;
  at_risk_count: number;
  total_share?: number;
  concentration_index?: number;
  overlap_score?: number;
  recommendations?: Array<{
    gtin: string;
    name: string;
    action: string;
    reason: string;
  }>;
  products?: Array<{
    gtin: string;
    name: string;
    health_score: number;
    volume_share: number;
    margin: number;
  }>;
}

export interface TurfAnalysis {
  current_reach: number;
  optimal_reach: number;
  optimal_sku_count: number;
  recommendations: Array<{
    gtin: string;
    name: string;
    action: 'keep' | 'add' | 'remove';
    incremental_reach: number;
  }>;
}

export interface CannibalizationAnalysis {
  overall_risk: 'low' | 'medium' | 'high';
  high_overlap_pairs: Array<{
    product1: string;
    product2: string;
    overlap_score: number;
  }>;
}

export interface ChannelLandscapeData {
  channels: Array<{
    id: string;
    name: string;
    share: number;
    growth: number;
    description?: string;
    characteristics?: string[];
    key_accounts?: Array<{ name: string; share: number }>;
  }>;
  distribution: {
    weighted_distribution: number;
    numeric_distribution: number;
    out_of_stock_rate: number;
    shelf_share?: number;
  };
  trade_dynamics: {
    average_margin: number;
    promo_intensity: number;
    private_label_share: number;
  };
  regional_distribution?: Record<string, { share: number; weighted_distribution: number }>;
}

// Cost Structure Types (FEMSA differentiated data)
export interface CostStructureData {
  has_data: boolean;
  source: 'proprietary' | 'baseline';
  data: {
    pnl?: {
      net_sales: { value_mxn: number; value_mxn_formatted: string; pct: number };
      cogs: { value_mxn: number; value_mxn_formatted: string; pct: number; notes?: string };
      gross_profit: { value_mxn: number; value_mxn_formatted: string; pct: number; notes?: string };
      operating_profit?: { value_mxn: number; value_mxn_formatted: string; pct: number; notes?: string };
    };
    key_metrics: {
      gross_margin: { actual: number; previous_estimate?: number; delta?: string; notes?: string };
      operating_margin?: { actual: number; previous_estimate?: number; delta?: string; notes?: string };
      cogs_ratio?: { actual: number; notes?: string };
    };
    b2b_margins?: Record<string, unknown>;
    input_costs?: Record<string, unknown>;
    margin_waterfall?: {
      steps: Array<{ step: string; value_pct: number; cumulative?: number }>;
    };
    metadata?: Record<string, unknown>;
    message?: string;
  };
}

// Seasonality Types (FEMSA differentiated data)
export interface SeasonalityData {
  has_data: boolean;
  source: 'proprietary' | 'baseline';
  data: {
    monthly_indices: Array<{
      month: number;
      name: string;
      index: number;
      vs_avg?: number;
      notes?: string;
    }>;
    summary: {
      highest_month: { month: string; index: number; driver?: string };
      lowest_month: { month: string; index: number; driver?: string };
      range: { high: number; low: number; spread: number; notes?: string };
      peaks?: Array<{ month: string; index: number; event: string }>;
      troughs?: Array<{ month: string; index: number; event: string }>;
    };
    quarterly?: Record<string, { months: string[]; avg_index: number; share_of_year: number; notes?: string }>;
    holiday_effects?: Array<{ event: string; timing: string; effect_pct: number; duration_days: number; notes?: string }>;
    weather_correlation?: Record<string, unknown>;
    metadata?: Record<string, unknown>;
    message?: string;
  };
}

// API Client
export const apiClient = {
  // Clients
  clients: {
    list: () => fetchJson<{ clients: ClientSummary[] }>('/api/clients'),
    getConfig: (clientId: string) => fetchJson<any>(`/api/clients/${clientId}/config`),
  },

  // Market
  market: {
    getOverview: (clientId: string) => fetchJson<MarketOverview>(`/api/clients/${clientId}/market/overview`),
    getSegments: (clientId: string) => fetchJson<SegmentAnalysis>(`/api/clients/${clientId}/market/segments`),
    getPosition: (clientId: string) => fetchJson<PortfolioPosition>(`/api/clients/${clientId}/market/position`),
    getChannels: (clientId: string) => fetchJson<ChannelLandscapeData>(`/api/clients/${clientId}/market/channels`),
    getCosts: (clientId: string) => fetchJson<CostStructureData>(`/api/clients/${clientId}/market/costs`),
    getSeasonality: (clientId: string) => fetchJson<SeasonalityData>(`/api/clients/${clientId}/market/seasonality`),
  },

  // Pricing
  pricing: {
    getElasticity: (clientId: string) => fetchJson<{ products: ProductElasticity[] }>(`/api/clients/${clientId}/pricing/elasticity`),
    getPortfolio: (clientId: string) => fetchJson<PortfolioPricing>(`/api/clients/${clientId}/pricing/portfolio`),
    getDemandCurve: (clientId: string, gtin: string) =>
      fetchJson<DemandCurve>(`/api/clients/${clientId}/pricing/demand-curve`, { params: { gtin } }),
  },

  // Launch
  launch: {
    getConfig: (clientId: string) => fetchJson<LaunchConfig>(`/api/clients/${clientId}/launch/forecast`),
    forecast: (clientId: string, data: any) =>
      fetchJson<LaunchForecast>(`/api/clients/${clientId}/launch/forecast`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
  },

  // Portfolio
  portfolio: {
    getHealth: (clientId: string) => fetchJson<PortfolioHealth>(`/api/clients/${clientId}/portfolio/health`),
    getTurf: (clientId: string) => fetchJson<TurfAnalysis>(`/api/clients/${clientId}/portfolio/turf`),
    getCannibalization: (clientId: string) => fetchJson<CannibalizationAnalysis>(`/api/clients/${clientId}/portfolio/cannibalization`),
    getRationalization: (clientId: string) => fetchJson<any>(`/api/clients/${clientId}/portfolio/rationalization`),
    getDueTo: (clientId: string) => fetchJson<DueToDecomposition>(`/api/clients/${clientId}/portfolio/due-to`),
  },

  // Opportunities
  opportunities: {
    get: (clientId: string, language: string = 'en') =>
      fetchJson<OpportunityEvaluationResult>(`/api/clients/${clientId}/opportunities?language=${language}`),
  },

  // Quality
  quality: {
    getAssessment: (clientId: string) => fetchJson<ClientQualityAssessment>(`/api/clients/${clientId}/quality`),
    getRecommendations: (clientId: string) => fetchJson<{ recommendations: DataRecommendation[] }>(`/api/clients/${clientId}/quality/recommendations`),
    getCapabilities: (clientId: string) => fetchJson<CapabilitySummary>(`/api/clients/${clientId}/quality/capabilities`),
  },

  // Simulations
  simulations: {
    run: (clientId: string, config: SimulationConfig) =>
      fetchJson<SimulationResult>(`/api/clients/${clientId}/simulations/run`, {
        method: 'POST',
        body: JSON.stringify(config),
      }),
  },

  // Generic get for custom endpoints
  get: <T>(path: string) => fetchJson<T>(`/api${path}`),
};

// Additional types for ImplicationsPage
export interface DueToDecomposition {
  period: { start: string; end: string };
  baseline_volume: number;
  actual_volume: number;
  volume_change: number;
  volume_change_pct: number;
  drivers: {
    distribution: { impact: number; contribution_pct: number };
    price: { impact: number; contribution_pct: number };
    velocity: { impact: number; contribution_pct: number };
    promotion: { impact: number; contribution_pct: number };
    mix: { impact: number; contribution_pct: number };
    unexplained: { impact: number; contribution_pct: number };
  };
  waterfall_data: Array<{
    label: string;
    value: number;
    running_total: number;
    type: 'increase' | 'decrease' | 'total';
  }>;
}

export interface EnhancedTURFResult {
  optimal_assortment: string[];
  reach: number;
  frequency: number;
  incremental_reach: Array<{ sku: string; added_reach: number }>;
  by_segment?: Array<{
    segment_id: string;
    segment_name: string;
    optimal_assortment: string[];
    reach: number;
    frequency: number;
    segment_coverage_gap: number;
  }>;
  portfolio_efficiency: number;
  diminishing_returns_threshold: number;
}

export interface RationalizationResult {
  scores: Array<{
    sku: string;
    composite_score: number;
    recommendation: 'keep' | 'monitor' | 'delist';
    rationale: string;
  }>;
  portfolio_analysis: {
    total_skus: number;
    keep_count: number;
    monitor_count: number;
    delist_count: number;
    potential_share_recovery: number;
  };
  recommendations: string[];
}

export interface CannibalizationResult {
  total_cannibalization_rate: number;
  high_risk_pairs: Array<{
    product_a: string;
    product_b: string;
    overlap: number;
  }>;
  products: string[];
}

export interface SensitivityMarketOverview {
  overview: {
    total_value: number;
    total_volume: number;
    value_growth: number;
    volume_growth: number;
  };
  top_brands: Array<{
    name: string;
    share: number;
    growth: number;
  }>;
}

// Quality types
export type QualityTier = 'Gold' | 'Silver' | 'Bronze' | 'None';
export type DataDomain = 'market_data' | 'consumer_data' | 'transaction_data' | 'portfolio_data' | 'competitive_data';

export interface DomainQualityScore {
  domain: DataDomain;
  score: number;
  tier: QualityTier;
  missingFields: string[];
  recommendations: string[];
}

export interface ClientQualityAssessment {
  clientId: string;
  overallScore: number;
  overallTier: QualityTier;
  domains: DomainQualityScore[];
}

export interface DataRecommendation {
  domain: DataDomain;
  action: string;
  impact: string;
  priority: 'critical' | 'high' | 'medium' | 'low';
}

export interface CapabilitySummary {
  overallTier: string;
  overallScore: number;
  capabilities: Array<{
    analysisType: string;
    available: boolean;
    modelUsed: string;
    degraded: boolean;
    unlockableWith: string[];
  }>;
}

// Opportunity types
export type OpportunityType = 'pricing' | 'launch' | 'segment' | 'distribution' | 'competitive' | 'portfolio' | 'regional' | 'promotion' | 'margin' | 'innovation';
export type ImpactLevel = 'critical' | 'high' | 'medium' | 'low';
export type ConfidenceLevel = 'high' | 'medium' | 'low';
export type TimeHorizon = 'immediate' | 'short_term' | 'medium_term' | 'long_term';

export interface EvaluatedOpportunity {
  id: string;
  ruleId: string;
  type: OpportunityType;
  title: string;
  description: string;
  impact: ImpactLevel;
  severityScore: number;
  confidence: ConfidenceLevel;
  timeHorizon: TimeHorizon;
  icon: string;
  action?: string;
  actionLink?: string;
  triggerData?: Record<string, number | string>;
  evaluatedAt: string;
  evidence?: string;
  affectedProducts?: string[];
}

export interface OpportunityEvaluationResult {
  clientId: string;
  evaluatedAt: string;
  rulesEvaluated: number;
  opportunitiesFound: number;
  byImpact: {
    critical: number;
    high: number;
    medium: number;
    low: number;
  };
  opportunities: EvaluatedOpportunity[];
  skippedRules?: string[];
}

// Simulation types
export interface SimulationAttribute {
  attribute_code: string;
  attribute_name: string;
  levels: Array<{
    level_code: string;
    level_name: string;
    level_description?: string;
    is_price_attribute?: boolean;
    value?: number | string;
  }>;
  selectedLevels?: string[];
}

export interface SimulationConfig {
  name: string;
  description?: string;
  simulation_type: string;
  attributes: SimulationAttribute[];
}

export interface ProductCombination {
  id: string;
  levels: Record<string, string>;
  utility: number;
  share: number;
  rank: number;
}

export interface SimulationResult {
  id: string;
  name: string;
  simulation_type: string;
  created_at: string;
  total_combinations: number;
  attributes_used: number;
  levels_used: number;
  top_products: ProductCombination[];
  all_products: ProductCombination[];
  attribute_importance: Record<string, number>;
  level_utilities: Record<string, Record<string, number>>;
  insights: string[];
}

// Chat types and API
export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp?: string;
}

export interface ChatResponse {
  response: string;
}

export const chatApi = {
  send: (message: string, clientId?: string, history?: ChatMessage[]) =>
    fetchJson<ChatResponse>('/api/chat', {
      method: 'POST',
      body: JSON.stringify({ message, clientId, history }),
    }),
};

// Explainability types
export interface AttributionFactor {
  name: string;
  displayName: string;
  contribution: number;
  direction: 'positive' | 'negative' | 'neutral';
  source: 'client_data' | 'model_estimate' | 'industry_benchmark' | 'assumption';
  description?: string;
  rawValue?: number | string;
}

export interface Assumption {
  name: string;
  displayName: string;
  value: string | number;
  sensitivity: 'high' | 'medium' | 'low';
  description?: string;
  suggestedRange?: { min: number; max: number };
}

export interface AttributionTrace {
  metricId: string;
  metricName: string;
  metricValue: number | string;
  unit?: string;
  methodology: string;
  formula?: string;
  factors: AttributionFactor[];
  assumptions: Assumption[];
  caveats?: string[];
  confidence?: 'high' | 'medium' | 'low';
  modelUsed?: string;
  degraded?: boolean;
}

export interface NaturalLanguageExplanation {
  summary: string;
  detailed: string;
  keyInsights: string[];
  suggestedActions?: string[];
}

export interface ExplanationResponse {
  metricId: string;
  attribution: AttributionTrace;
  naturalLanguage: NaturalLanguageExplanation;
  dataQuality?: {
    tier: string;
    score: number;
    limitationsApplied: string[];
  };
  generatedAt: string;
}

export const explainApi = {
  explain: (metricId: string, clientId: string, context?: Record<string, unknown>) =>
    fetchJson<ExplanationResponse>('/api/explain', {
      method: 'POST',
      body: JSON.stringify({ metricId, clientId, depth: 'detailed', context }),
    }),
};

// Scenario types
export type ScenarioType = 'launch' | 'pricing' | 'portfolio' | 'market';
export type ScenarioStatus = 'draft' | 'running' | 'completed' | 'failed';

export interface Scenario {
  id: string;
  clientId: string;
  name: string;
  description?: string;
  scenarioType: ScenarioType;
  config: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  isBaseline: boolean;
  parentScenarioId?: string;
  version: number;
  status: ScenarioStatus;
  tags?: string[];
  latestResult?: ScenarioResult;
}

export interface ScenarioResult {
  id: string;
  scenarioId: string;
  computedAt: string;
  durationMs?: number;
  results: Record<string, unknown>;
  metrics?: Record<string, number | string>;
  qualityTier?: string;
  modelUsed?: string;
}

export interface CreateScenarioInput {
  name: string;
  description?: string;
  scenarioType: ScenarioType;
  config: Record<string, unknown>;
  isBaseline?: boolean;
  tags?: string[];
}

export interface UpdateScenarioInput {
  name?: string;
  description?: string;
  config?: Record<string, unknown>;
  tags?: string[];
  status?: ScenarioStatus;
}

export interface ScenarioComparison {
  id: string;
  clientId: string;
  name: string;
  description?: string;
  scenarioIds: string[];
  baselineScenarioId?: string;
  createdAt: string;
  updatedAt: string;
}

// Scenarios API
export const scenariosApi = {
  list: (clientId: string) =>
    fetchJson<{ scenarios: Scenario[] }>(`/api/clients/${clientId}/scenarios`),

  get: (clientId: string, scenarioId: string) =>
    fetchJson<Scenario>(`/api/clients/${clientId}/scenarios/${scenarioId}`),

  create: (clientId: string, data: CreateScenarioInput) =>
    fetchJson<Scenario>(`/api/clients/${clientId}/scenarios`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (clientId: string, scenarioId: string, data: UpdateScenarioInput) =>
    fetchJson<Scenario>(`/api/clients/${clientId}/scenarios/${scenarioId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  delete: (clientId: string, scenarioId: string) =>
    fetchJson<{ success: boolean }>(`/api/clients/${clientId}/scenarios/${scenarioId}`, {
      method: 'DELETE',
    }),

  duplicate: (clientId: string, scenarioId: string, name?: string) =>
    fetchJson<Scenario>(`/api/clients/${clientId}/scenarios/${scenarioId}/duplicate`, {
      method: 'POST',
      body: JSON.stringify({ name }),
    }),

  run: (clientId: string, scenarioId: string) =>
    fetchJson<ScenarioResult>(`/api/clients/${clientId}/scenarios/${scenarioId}/run`, {
      method: 'POST',
    }),

  // Comparisons
  listComparisons: (clientId: string) =>
    fetchJson<{ comparisons: ScenarioComparison[] }>(`/api/clients/${clientId}/comparisons`),

  createComparison: (clientId: string, data: { name: string; scenarioIds: string[] }) =>
    fetchJson<ScenarioComparison>(`/api/clients/${clientId}/comparisons`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
};

export { ApiError };
